import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { addDays, format, startOfDay } from "date-fns";

// Fixed shift slots
export const SHIFT_SLOTS = [
  { id: "evening", label: "Evening Shift", start: "21:00", end: "02:00", defaultRate: 90 },
  { id: "morning", label: "Morning Shift", start: "05:00", end: "10:00", defaultRate: 60 },
] as const;

export type ShiftSlotId = typeof SHIFT_SLOTS[number]["id"];

interface DriverShift {
  id: string;
  driver_id: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  status: string;
}

interface UseDriverShiftBookingResult {
  // Driver info
  driverId: string | null;
  driverName: string | null;
  
  // Shift data
  myUpcomingShifts: DriverShift[];
  myPastShifts: DriverShift[];
  remainingShiftsCount: number;
  shouldPromptForBooking: boolean; // < 5 shifts - show popup
  isUrgent: boolean; // <= 3 shifts - show persistent banner
  isCritical: boolean; // <= 1 shift - most urgent
  
  // Availability for next 30 days
  availableSlots: Array<{
    date: string;
    dateLabel: string;
    slots: Array<{
      slotId: ShiftSlotId;
      label: string;
      start: string;
      end: string;
      rate: number;
      isBooked: boolean;
      bookedBy: string | null;
      isMyBooking: boolean;
    }>;
  }>;
  
  // Actions
  bookShift: (date: string, slotId: ShiftSlotId) => Promise<void>;
  bookMultipleShifts: (shifts: Array<{ date: string; slotId: ShiftSlotId }>) => Promise<{ success: number; failed: number }>;
  cancelShift: (shiftId: string, shiftData: { shift_date: string; start_time: string; end_time: string }) => Promise<void>;
  
  // State
  isLoading: boolean;
  isBooking: boolean;
}

export const useDriverShiftBooking = (): UseDriverShiftBookingResult => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Get driver info for logged-in user
  const { data: driverData, isLoading: driverLoading } = useQuery({
    queryKey: ["my-driver-record", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("drivers")
        .select("id, full_name, driver_number")
        .eq("user_id", user.id)
        .eq("active", true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    staleTime: 300000,
  });
  
  const today = startOfDay(new Date());
  const next30Days = Array.from({ length: 30 }, (_, i) => format(addDays(today, i + 1), "yyyy-MM-dd"));
  
  // Get all shifts for next 30 days (to see what's booked)
  const { data: allShiftsData, isLoading: shiftsLoading } = useQuery({
    queryKey: ["all-shifts-next-30-days", next30Days[0], next30Days[29]],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("driver_shifts")
        .select(`
          id,
          driver_id,
          shift_date,
          start_time,
          end_time,
          status,
          drivers!inner(full_name)
        `)
        .gte("shift_date", next30Days[0])
        .lte("shift_date", next30Days[29])
        .neq("status", "cancelled")
        .order("shift_date", { ascending: true });
      
      if (error) throw error;
      return data as any[];
    },
    enabled: !!driverData?.id,
    staleTime: 30000,
    refetchInterval: 30000,
  });
  
  // Get MY upcoming shifts (for remaining count)
  const { data: myShiftsData, isLoading: myShiftsLoading } = useQuery({
    queryKey: ["my-upcoming-shifts", driverData?.id],
    queryFn: async () => {
      if (!driverData?.id) return [];
      
      const todayStr = format(today, "yyyy-MM-dd");
      const { data, error } = await supabase
        .from("driver_shifts")
        .select("*")
        .eq("driver_id", driverData.id)
        .gte("shift_date", todayStr)
        .neq("status", "cancelled")
        .order("shift_date", { ascending: true });
      
      if (error) throw error;
      return data as DriverShift[];
    },
    enabled: !!driverData?.id,
    staleTime: 30000,
    refetchInterval: 30000,
  });
  
  // Get MY past shifts (last 30 days for history)
  const { data: myPastShiftsData, isLoading: myPastShiftsLoading } = useQuery({
    queryKey: ["my-past-shifts", driverData?.id],
    queryFn: async () => {
      if (!driverData?.id) return [];
      
      const todayStr = format(today, "yyyy-MM-dd");
      const thirtyDaysAgo = format(addDays(today, -30), "yyyy-MM-dd");
      const { data, error } = await supabase
        .from("driver_shifts")
        .select("*")
        .eq("driver_id", driverData.id)
        .gte("shift_date", thirtyDaysAgo)
        .lt("shift_date", todayStr)
        .order("shift_date", { ascending: false });
      
      if (error) throw error;
      return data as DriverShift[];
    },
    enabled: !!driverData?.id,
    staleTime: 60000,
  });
  
  const myUpcomingShifts = myShiftsData || [];
  const myPastShifts = myPastShiftsData || [];
  const remainingShiftsCount = myUpcomingShifts.length;
  
  // Thresholds
  const shouldPromptForBooking = remainingShiftsCount < 5 && !!driverData?.id;
  const isUrgent = remainingShiftsCount <= 3 && !!driverData?.id;
  const isCritical = remainingShiftsCount <= 1 && !!driverData?.id;

  // Shift notifications are now batched into a daily digest sent at 8 AM UK time
  // No real-time email notifications for bookings, cancellations, or low-shift warnings
  
  // Book a shift mutation
  const bookMutation = useMutation({
    mutationFn: async ({ date, slotId }: { date: string; slotId: ShiftSlotId }) => {
      if (!driverData?.id) throw new Error("No driver record found");
      
      const slot = SHIFT_SLOTS.find(s => s.id === slotId);
      if (!slot) throw new Error("Invalid slot");
      
      // Double-check no one else booked it (race condition protection)
      const { data: existing, error: checkError } = await supabase
        .from("driver_shifts")
        .select("id, drivers!inner(full_name)")
        .eq("shift_date", date)
        .eq("start_time", slot.start)
        .neq("status", "cancelled")
        .maybeSingle();
      
      if (checkError) throw checkError;
      if (existing) {
        throw new Error(`This slot was just booked by ${(existing as any).drivers?.full_name || 'another driver'}`);
      }
      
      // Insert the shift
      const { error: insertError } = await supabase
        .from("driver_shifts")
        .insert({
          driver_id: driverData.id,
          shift_date: date,
          start_time: slot.start,
          end_time: slot.end,
          status: "scheduled",
          notes: "Self-booked by driver"
        });
      
      if (insertError) throw insertError;
      
      // Email notifications are batched into daily digest at 8 AM
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-shifts-next-30-days"] });
      queryClient.invalidateQueries({ queryKey: ["my-upcoming-shifts"] });
    },
  });
  
  // Cancel a shift mutation
  const cancelMutation = useMutation({
    mutationFn: async ({ shiftId, shiftData }: { shiftId: string; shiftData: { shift_date: string; start_time: string; end_time: string } }) => {
      const { error } = await supabase
        .from("driver_shifts")
        .update({ status: "cancelled" })
        .eq("id", shiftId);
      
      if (error) throw error;
      
      // Email notifications are batched into daily digest at 8 AM
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-shifts-next-30-days"] });
      queryClient.invalidateQueries({ queryKey: ["my-upcoming-shifts"] });
    },
  });
  
  // Process availability
  const availableSlots = next30Days.map(date => {
    const dateObj = new Date(date);
    const dayName = format(dateObj, "EEEE");
    const dateLabel = `${dayName}, ${format(dateObj, "MMM d")}`;
    
    const slots = SHIFT_SLOTS.map(slot => {
      const bookedShift = allShiftsData?.find(s => 
        s.shift_date === date && 
        s.start_time.startsWith(slot.start)
      );
      
      return {
        slotId: slot.id,
        label: slot.label,
        start: slot.start,
        end: slot.end,
        rate: slot.defaultRate,
        isBooked: !!bookedShift,
        bookedBy: bookedShift?.drivers?.full_name || null,
        isMyBooking: bookedShift?.driver_id === driverData?.id,
      };
    });
    
    return { date, dateLabel, slots };
  });
  
  return {
    driverId: driverData?.id || null,
    driverName: driverData?.full_name || null,
    myUpcomingShifts,
    myPastShifts,
    remainingShiftsCount,
    shouldPromptForBooking,
    isUrgent,
    isCritical,
    availableSlots,
    bookShift: async (date, slotId) => {
      await bookMutation.mutateAsync({ date, slotId });
    },
    bookMultipleShifts: async (shifts) => {
      let success = 0;
      let failed = 0;
      
      for (const shift of shifts) {
        try {
          await bookMutation.mutateAsync({ date: shift.date, slotId: shift.slotId });
          success++;
        } catch (e) {
          console.error('Failed to book shift:', e);
          failed++;
        }
      }
      
      // Invalidate queries after all bookings
      queryClient.invalidateQueries({ queryKey: ["all-shifts-next-30-days"] });
      queryClient.invalidateQueries({ queryKey: ["my-upcoming-shifts"] });
      
      return { success, failed };
    },
    cancelShift: async (shiftId, shiftData) => {
      await cancelMutation.mutateAsync({ shiftId, shiftData });
    },
    isLoading: driverLoading || shiftsLoading || myShiftsLoading || myPastShiftsLoading,
    isBooking: bookMutation.isPending || cancelMutation.isPending,
  };
};
